import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ShieldCheck } from 'lucide-react';
import Logo from '../components/Logo';
import { useAuth } from '../contexts/AuthContext';

// A3 — dış kartın üyeliğe davet bağlantısı (/kart-davet#t=...). Token URL parçasındadır: tarayıcı onu sunucuya
// adres olarak göndermez; sayfa açılır açılmaz adres çubuğundan ve geçmişten silinir. Yetkiler sunucuda.
const API = `${process.env.REACT_APP_BACKEND_URL}/api/claim-invites`;
const TOKEN = /^[A-Za-z0-9_-]{20,200}$/;
const inputClass = 'w-full px-4 py-3 border border-zinc-200 rounded-xl bg-white';
const INVALID = 'Bu davet bağlantısı artık geçerli değil. Süresi dolmuş, iptal edilmiş ya da daha önce kullanılmış olabilir. Yeni bağlantı için seni davet eden ekibe yaz.';
const MISSING = 'Davet bağlantısı eksik veya hatalı. Sana iletilen bağlantıyı eksiksiz açtığından emin ol.';
const ROLE_WORD = { venue: 'mekân', organizer: 'organizatör' };

const readToken = () => new URLSearchParams(window.location.hash.slice(1)).get('t') || '';

export const claimErrorText = error => {
  if (['ECONNABORTED', 'ETIMEDOUT'].includes(error?.code)) return 'Sunucu zamanında yanıt vermedi. Biraz sonra tekrar dene.';
  if (!error?.response) return 'Sunucuya ulaşılamadı. İnternet bağlantını kontrol edip tekrar dene.';
  const { status, data } = error.response;
  const detail = data?.detail;
  if (status === 410) return INVALID;
  if (status === 429) return 'Çok fazla deneme yapıldı. Lütfen 10 dakika sonra tekrar dene.';
  if (typeof detail === 'string') return detail;
  if (detail?.message) return detail.message;
  if (status === 422) return 'Bilgileri kontrol et: ad en az 2, şifre en az 8 karakter olmalı ve e-posta geçerli olmalı.';
  if (status === 503) return 'Kart davetleri şu anda kapalı. Bir süre sonra tekrar dene.';
  return 'İşlem tamamlanamadı. Lütfen tekrar dene.';
};

const passwordProblem = (password, repeat) => {
  if (password.length < 8 || new TextEncoder().encode(password).length > 72) return 'Şifre en az 8 karakter olmalı ve çok uzun olmamalı.';
  if (repeat !== undefined && password !== repeat) return 'Şifreler eşleşmiyor.';
  return '';
};

function CardSummary({ card }) {
  const area = card.entity_type === 'venue' ? card.district : (card.district_labels || []).join(', ');
  return <div className="admin-member-card mb-4" data-testid="claim-card-summary">
    <p className="text-sm admin-pool-labels"><span className="status-pill">{card.label}</span><span className="status-pill">{card.role_label}</span></p>
    <strong className="block text-lg mt-1" data-testid="claim-card-name">{card.name}</strong>
    <p className="text-sm">{[(card.event_type_labels || []).join(', '), area].filter(Boolean).join(' · ')}</p>
    {card.instagram_handle && <p className="text-sm">@{card.instagram_handle}</p>}
  </div>;
}

export default function ClaimInvite() {
  const navigate = useNavigate();
  const { user, login, acceptSession } = useAuth();
  const [token] = useState(readToken);
  const valid = TOKEN.test(token);
  const [card, setCard] = useState(null);
  const [status, setStatus] = useState(valid ? 'loading' : 'error');
  const [error, setError] = useState(valid ? '' : MISSING);
  const [mode, setMode] = useState('register');
  const [form, setForm] = useState({ name: '', email: '', password: '', repeat: '' });
  const [busy, setBusy] = useState(false);
  const submitting = useRef(false);
  const set = (key, value) => setForm(f => ({ ...f, [key]: value }));

  useEffect(() => {
    // Tek kullanımlık tokenı adres çubuğundan ve geçmişten hemen sil.
    if (window.location.hash) window.history.replaceState(null, '', window.location.pathname);
    if (!valid) return undefined;
    let alive = true;
    axios.post(`${API}/check`, { token }, { timeout: 15000 })
      .then(({ data }) => { if (!alive) return; setCard(data.card); setStatus('ready'); })
      .catch(err => { if (!alive) return; setError(claimErrorText(err)); setStatus('error'); });
    return () => { alive = false; };
  }, [token, valid]);

  useEffect(() => { if (user && status === 'ready') setMode('existing'); }, [user, status]);

  const finish = claim => {
    toast.success(claim?.completed ? 'Kart üyelik profiline bağlandı.' : 'Kart hesabına bağlandı. Şimdi Meko ile profilini tamamlayalım.');
    navigate(claim?.next || '/', { replace: true });
  };

  const fail = err => {
    const code = err?.response?.data?.detail?.code;
    setError(claimErrorText(err));
    if (err?.response?.status === 410) setStatus('error');
    if (code === 'EMAIL_ALREADY_REGISTERED') { setMode('existing'); }
  };

  const run = async job => {
    if (submitting.current) return;
    submitting.current = true; setBusy(true); setError('');
    try { await job(); } catch (err) { fail(err); } finally { submitting.current = false; setBusy(false); }
  };

  const register = event => {
    event.preventDefault();
    if (form.name.trim().length < 2) { setError('Adını yaz.'); return; }
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) { setError('Geçerli bir e-posta yaz.'); return; }
    const problem = passwordProblem(form.password, form.repeat);
    if (problem) { setError(problem); return; }
    run(async () => {
      const { data } = await axios.post(`${API}/redeem-register`, { token, name: form.name.trim(),
        email: form.email.trim().toLowerCase(), password: form.password }, { timeout: 20000 });
      acceptSession(data);
      finish(data.claim);
    });
  };

  const redeem = async () => {
    const { data } = await axios.post(`${API}/redeem`, { token }, { timeout: 20000 });
    finish(data.claim);
  };

  const loginAndRedeem = event => {
    event.preventDefault();
    if (!form.email.trim() || !form.password) { setError('E-posta ve şifreni yaz.'); return; }
    run(async () => {
      const result = await login(form.email.trim().toLowerCase(), form.password);
      if (!result?.success) { setError(result?.error || 'E-posta veya şifre hatalı.'); return; }
      await redeem();
    });
  };

  const roleWord = ROLE_WORD[card?.entity_type] || 'profil';
  return <main className="min-h-screen flex items-center justify-center bg-[#FAFAFA] px-4 py-10">
    <div className="w-full max-w-md" data-testid="claim-invite-page">
      <div className="mb-6 flex justify-center"><Logo size="xl" variant="mark" /></div>
      <div className="bg-white border border-zinc-200 rounded-2xl p-6 shadow-sm">
        <h1 className="text-2xl font-semibold mb-2">Kartını sahiplen</h1>
        {status === 'loading' && <p role="status">Davet kontrol ediliyor…</p>}
        {status === 'error' && <>
          <p role="alert" className="text-sm" style={{ color: 'var(--danger-text, #b42318)' }} data-testid="claim-error">{error}</p>
          <Link to="/login" className="inline-block mt-6 text-sm underline">Giriş sayfasına dön</Link>
        </>}
        {status === 'ready' && card && <>
          <p className="text-sm text-zinc-500 mb-4">MekandaPro ekibi seni bu {roleWord} kartının sahibi olarak davet etti. Hesabını açınca kart sana bağlanır; Meko ile profilini tamamlayıp onaya gönderirsin.</p>
          <CardSummary card={card} />
          <div className="admin-member-actions mb-4" role="tablist" aria-label="Devam etme yolu">
            <button type="button" role="tab" aria-selected={mode === 'register'} className={mode === 'register' ? 'action-primary' : 'action-secondary'}
              onClick={() => { setMode('register'); setError(''); }} data-testid="claim-mode-register">Yeni hesap aç</button>
            <button type="button" role="tab" aria-selected={mode === 'existing'} className={mode === 'existing' ? 'action-primary' : 'action-secondary'}
              onClick={() => { setMode('existing'); setError(''); }} data-testid="claim-mode-existing">Hesabım var</button>
          </div>
          {mode === 'register' && <form onSubmit={register} className="space-y-4" noValidate data-testid="claim-register-form">
            <label className="block text-sm">Ad soyad
              <input className={`${inputClass} mt-1`} value={form.name} onChange={e => set('name', e.target.value)} maxLength={120}
                autoComplete="name" required data-testid="claim-register-name" /></label>
            <label className="block text-sm">E-posta
              <input className={`${inputClass} mt-1`} type="email" value={form.email} onChange={e => set('email', e.target.value)}
                autoComplete="email" required data-testid="claim-register-email" /></label>
            <label className="block text-sm">Şifre
              <input className={`${inputClass} mt-1`} type="password" value={form.password} onChange={e => set('password', e.target.value)}
                autoComplete="new-password" minLength={8} required data-testid="claim-register-password" /></label>
            <label className="block text-sm">Şifre (tekrar)
              <input className={`${inputClass} mt-1`} type="password" value={form.repeat} onChange={e => set('repeat', e.target.value)}
                autoComplete="new-password" minLength={8} required data-testid="claim-register-repeat" /></label>
            {error && <p role="alert" className="text-sm" style={{ color: 'var(--danger-text, #b42318)' }} data-testid="claim-form-error">{error}</p>}
            <button type="submit" disabled={busy} className="action-primary w-full" data-testid="claim-register-submit">
              {busy ? 'Hesap açılıyor…' : `Hesabı aç ve kartı sahiplen`}</button>
          </form>}
          {mode === 'existing' && user && <div className="space-y-4" data-testid="claim-current-user">
            <p className="text-sm">Şu an <strong className="break-all">{user.email || user.name}</strong> hesabıyla giriş yaptın. Kart bu hesaba bağlanır; ikinci hesap veya profil açılmaz.</p>
            {error && <p role="alert" className="text-sm" style={{ color: 'var(--danger-text, #b42318)' }} data-testid="claim-form-error">{error}</p>}
            <button type="button" disabled={busy} className="action-primary w-full" onClick={() => run(redeem)} data-testid="claim-use-current">
              {busy ? 'Bağlanıyor…' : 'Bu hesapla kartı sahiplen'}</button>
          </div>}
          {mode === 'existing' && !user && <form onSubmit={loginAndRedeem} className="space-y-4" noValidate data-testid="claim-login-form">
            <p className="text-sm text-zinc-500">Giriş yap; kart mevcut {roleWord} hesabına bağlansın. Google ile giriş yapıyorsan önce giriş yapıp bu bağlantıyı yeniden aç.</p>
            <label className="block text-sm">E-posta
              <input className={`${inputClass} mt-1`} type="email" value={form.email} onChange={e => set('email', e.target.value)}
                autoComplete="email" required data-testid="claim-login-email" /></label>
            <label className="block text-sm">Şifre
              <input className={`${inputClass} mt-1`} type="password" value={form.password} onChange={e => set('password', e.target.value)}
                autoComplete="current-password" required data-testid="claim-login-password" /></label>
            {error && <p role="alert" className="text-sm" style={{ color: 'var(--danger-text, #b42318)' }} data-testid="claim-form-error">{error}</p>}
            <button type="submit" disabled={busy} className="action-primary w-full" data-testid="claim-login-submit">
              {busy ? 'Giriş yapılıyor…' : 'Giriş yap ve kartı sahiplen'}</button>
          </form>}
          <p className="text-xs text-zinc-500 mt-4 flex items-start gap-1"><ShieldCheck size={14} aria-hidden="true" />
            Davet bağlantısı tek kullanımlıktır ve 14 gün geçerlidir. Profilin ekibimizin onayından sonra keşifte görünür.</p>
        </>}
      </div>
    </div>
  </main>;
}
